import dayjs from 'dayjs';
import getReadings from '../dataServices';
import { ChartFiltersWeather } from '../types';
import {
  buildCategoriesFromDates,
} from '../utils';

interface DateRange {
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
}

export interface YearConfig {
  rainfallStartMonth: number;
  rainfallStartDay: number;
  rainfallEndMonth: number;
  rainfallEndDay: number;
}

export interface RainfallConfigByYear {
  [year: string]: YearConfig;
}

const buildDateRangesFromConfig = (
  configByYear: RainfallConfigByYear,
  referenceStartDate: string,
  referenceEndDate: string,
): DateRange[] => {
  const startRef = dayjs(referenceStartDate).utc().startOf('day');
  const endRef = dayjs(referenceEndDate).utc().endOf('day');
  const ranges: DateRange[] = [];

  const startYear = startRef.year();
  const endYear = endRef.year();

  for (let year = startYear; year <= endYear; year += 1) {
    const yearStr = year.toString();
    const config = configByYear[yearStr];

    if (config) {
      const {
        rainfallStartMonth, rainfallStartDay, rainfallEndMonth, rainfallEndDay,
      } = config;

      const crossesYears = rainfallEndMonth < rainfallStartMonth
        || (rainfallEndMonth === rainfallStartMonth && rainfallEndDay < rainfallStartDay);

      let rangeStart = dayjs().utc().year(year).month(rainfallStartMonth - 1)
        .date(rainfallStartDay)
        .startOf('day');
      let rangeEnd: dayjs.Dayjs;

      if (crossesYears) {
        rangeEnd = dayjs().utc().year(year + 1).month(rainfallEndMonth - 1)
          .date(rainfallEndDay)
          .endOf('day');
      } else {
        rangeEnd = dayjs().utc().year(year).month(rainfallEndMonth - 1)
          .date(rainfallEndDay)
          .endOf('day');
      }

      if (rangeStart.isBefore(startRef)) {
        rangeStart = startRef;
      }

      if (rangeEnd.isAfter(endRef)) {
        rangeEnd = endRef;
      }

      if (rangeStart.isSameOrBefore(rangeEnd) && rangeEnd.isSameOrAfter(startRef)) {
        ranges.push({
          startDate: rangeStart,
          endDate: rangeEnd,
        });
      }
    }
  }

  return ranges;
};

const isDateInRange = (date: dayjs.Dayjs, ranges: DateRange[]): boolean => (
  ranges.some((range) => date.isSameOrAfter(range.startDate, 'day')
    && date.isSameOrBefore(range.endDate, 'day'))
);

const calculateRainSeriesData = (
  data: Record<string, number>,
  days: string[],
) => {
  const rainData = days.map((day) => {
    const dayData = data[day];

    if (dayData === undefined || dayData === null) return null;

    return {
      x: dayjs(day).valueOf(),
      y: dayData,
      custom: {
        date: dayjs(day).format('YYYY-MM-DD'),
      },
    };
  });
  return rainData;
};

const calculateAccumulatedRainData = (
  data: Record<string, number>,
  days: string[],
  configByYear?: RainfallConfigByYear,
  displayStartDate?: string,
  displayEndDate?: string,
) => {
  if (!configByYear || !displayStartDate || !displayEndDate
    || Object.keys(configByYear).length === 0) {
    let cumulativeSum = 0;
    return days.map((day) => {
      const dayData = data[day];
      if (dayData === undefined || dayData === null) return null;
      cumulativeSum += dayData;
      return {
        x: dayjs(day).valueOf(),
        y: cumulativeSum,
        custom: {
          date: dayjs(day).format('YYYY-MM-DD'),
        },
      };
    });
  }

  const dateRanges = buildDateRangesFromConfig(
    configByYear,
    displayStartDate,
    displayEndDate,
  );

  const accumulatedByRange: Map<number, number> = new Map();

  dateRanges.forEach((range) => {
    let rangeAccumulator = 0;
    days.forEach((day) => {
      const dayDate = dayjs(day);
      if (dayDate.isSameOrAfter(range.startDate, 'day')
        && dayDate.isSameOrBefore(range.endDate, 'day')) {
        const dayData = data[day];
        if (dayData !== undefined && dayData !== null) {
          rangeAccumulator += dayData;
        }
        accumulatedByRange.set(dayDate.valueOf(), rangeAccumulator);
      }
    });
  });

  return days.map((day) => {
    const dayDate = dayjs(day);
    const isInRange = isDateInRange(dayDate, dateRanges);

    if (!isInRange) {
      return {
        x: dayDate.valueOf(),
        y: 0,
        custom: {
          date: dayDate.format('YYYY-MM-DD'),
        },
      };
    }

    const accumulated = accumulatedByRange.get(dayDate.valueOf());
    return {
      x: dayDate.valueOf(),
      y: accumulated !== undefined ? accumulated : 0,
      custom: {
        date: dayDate.format('YYYY-MM-DD'),
      },
    };
  });
};

const prepareData = async (
  filters: ChartFiltersWeather,
  telemetryType?: string,
  configByYear?: RainfallConfigByYear,
) => {
  const granularity = 'daily';

  const rainData = await getReadings({
    ...filters,
    wiseconnZoneId: filters.weatherZoneId,
    sensorType: 'Rain',
    granularity,
    telemetryType,
    sn: telemetryType === 'sn' ? filters.weatherZoneId : undefined,
    chart: filters.chart,
  });

  const rainDataMap: Record<string, number> = {};

  rainData.forEach((rain: { date: string; cumulativeTotal: number }) => {
    rainDataMap[rain.date] = rain.cumulativeTotal;
  });

  const absoluteCategories = buildCategoriesFromDates(
    filters.startDate, filters.endDate,
  );

  const rainChartData = calculateRainSeriesData(rainDataMap, absoluteCategories);
  const cumulativeRainData = calculateAccumulatedRainData(
    rainDataMap,
    absoluteCategories,
    configByYear,
    filters.startDate,
    filters.endDate,
  );

  return {
    rain: rainChartData,
    cumulativeRain: cumulativeRainData,
    absoluteCategories,
  };
};

export default prepareData;
